import { getPublicConfig } from '../../@data-loaders/get-public-config';
import type { GlobalImport, MhElement, PublicConfig, WidgetConfig } from '../../shared/interfaces';
import { toFeedback } from '../../shared/to-feedback';
import { createMissingElements } from '../@dom/create-missing-elements';
import { getElements } from '../@dom/get-elements';
import { getFirstForm } from '../@dom/get-first-form';
import { getInfo } from '../@dom/get-info';
import { mutateForm } from '../@dom/mutate-form';
import { setupElements } from '../@dom/setup-elements';

import { getWidgetConfig } from './get-widget-config';
import { loadIntegrations } from './load-integrations';

export abstract class FormWidgetBase extends HTMLElement {
    // Available after connectedCallback().
    config!: WidgetConfig;
    elements!: Record<string, MhElement>;
    form!: HTMLFormElement;
    button!: HTMLButtonElement | HTMLInputElement;
    publicConfig!: PublicConfig;
    importing!: Promise<HTMLScriptElement[]>;
    feedback!: HTMLParagraphElement;
    loader!: SVGElement;

    optionalInfo?: string[];
    requiredInfo?: string[];
    requiredImports?: GlobalImport[];

    async connectedCallback() {
        this.importing = loadIntegrations(this.requiredImports ?? []);

        this.form = getFirstForm(this);
        const formElements = await mutateForm(this.form).catch((err) => {
            toFeedback([err], this.feedback);
            throw err;
        });

        ({ button: this.button, feedback: this.feedback, loader: this.loader } = formElements);
        this.button.disabled = true;

        this.config = await getWidgetConfig(this).catch((err) => {
            toFeedback([err.message], this.feedback);
            throw err;
        });

        this.elements = getElements(this.form);
        createMissingElements(this.form, this.elements);

        this.publicConfig = await getPublicConfig();
        await setupElements(this.elements, this.publicConfig, this.config, this.form).catch((err) => {
            toFeedback([err], this.feedback);
            throw err;
        });

        this.button.onclick = async (event) => {
            try {
                event.preventDefault();
                const formValid = this.form.reportValidity();
                if (!formValid) {
                    this.feedback.innerHTML = `Please fill in all required fields.`;
                    return;
                }

                this.button.disabled = true;

                const info = await getInfo(this.requiredInfo ?? [], this.optionalInfo ?? [], this.elements).catch((err: Error) => {
                    toFeedback([err.message], this.feedback);
                    this.button.disabled = false;
                    throw err;
                });

                this.feedback.innerHTML = `&nbsp;`;

                await this.submit(info)
                    .then(() => {
                        this.dispatchEvent(new Event(`mh-success`));
                    })
                    .catch((err) => {
                        let innerHTML = err.message;
                        if (err.fields?.length) {
                            innerHTML += ` Invalid fields: ${err.fields.map((field: string) => `<b>${field}</b>`).join(`, `)}.`;
                        }
                        this.feedback.innerHTML = innerHTML;
                        this.dispatchEvent(new Event(`mh-error`));
                    });

                this.button.disabled = false;
            } catch (err) {
                this.button.disabled = false;
                this.feedback.innerHTML = `We're sorry, but something went wrong. Please try again later or contact support for assistance.`;
                console.error(err);
            }
        };

        await this.afterConnected();
        this.dispatchEvent(new Event(`mh-ready`));
    }

    async afterConnected() {
        this.button.disabled = false;
        this.loader.classList.add(`invisible`);
    }

    abstract submit(info: any): Promise<void>;
}
